import { Task, TaskPriority, TaskStatus, TaskStatusCategory, TaskType, TaskVisibility } from '@/types';

import { apiClient } from '../http';
import { createResourceService, QueryParams } from './resources';

export type TaskBuckets = Record<string, Task[]>;

export interface TaskCreateInput {
    associations?: Record<string, unknown>[];
    completed?: boolean;
    description?: string;
    dueAt?: Date | null;
    priority?: TaskPriority | null;
    assigneeMemberIds?: string[];
    reviewerMemberIds?: string[];
    statusId?: string | null;
    title: string;
    type?: TaskType | null;
    visibility?: TaskVisibility;
}

export type TaskUpdateInput = Partial<TaskCreateInput>;

export type TaskBulkActionInput =
    | { action: 'delete'; taskIds: string[] }
    | { action: 'setCompleted'; completed: boolean; taskIds: string[] }
    | { action: 'setPriority'; priority: TaskPriority | null; taskIds: string[] }
    | { action: 'setStatus'; statusId: string; taskIds: string[] };

export type TaskBulkActionResult =
    | { action: 'delete'; taskIds: string[] }
    | { action: 'setCompleted' | 'setPriority' | 'setStatus'; tasks: Task[] };

export interface TaskStatusCreateInput {
    category: TaskStatusCategory;
    color: string;
    name: string;
}

export type TaskStatusUpdateInput = Partial<TaskStatusCreateInput>;
export type TaskStatusConfigurationInput = {
    statuses: Array<TaskStatusCreateInput & { id?: string }>;
};

const base = createResourceService<Task, TaskCreateInput, TaskUpdateInput>('/api/tasks');

export const taskService = {
    ...base,
    list: <TParams extends QueryParams = QueryParams>(params?: TParams) => base.list(params),
    listBuckets: <TParams extends QueryParams = QueryParams>(params?: TParams) =>
        apiClient.get<TaskBuckets, TParams>('/api/tasks', { params }),
    bulkAction: (input: TaskBulkActionInput) =>
        apiClient.put<TaskBulkActionResult, TaskBulkActionInput>('/api/tasks/bulk/actions', input),
    setCompleted: (id: string, completed: boolean) =>
        apiClient.put<Task, { completed: boolean }>(`/api/tasks/${id}/completed`, { completed }),
    setPriority: (id: string, priority: TaskPriority | '') =>
        apiClient.put<Task, { priority: TaskPriority | '' }>(`/api/tasks/${id}/priority`, { priority }),
    setStatus: (id: string, statusId: string) =>
        apiClient.put<Task, { statusId: string }>(`/api/tasks/${id}/status`, { statusId }),
    statuses: {
        list: () => apiClient.get<TaskStatus[]>('/api/tasks/statuses'),
        create: (input: TaskStatusCreateInput) =>
            apiClient.post<TaskStatus, TaskStatusCreateInput>('/api/tasks/statuses', input),
        update: (statusId: string, input: TaskStatusUpdateInput) =>
            apiClient.put<TaskStatus, TaskStatusUpdateInput>(`/api/tasks/statuses/${statusId}`, input),
        reorder: (statusIds: string[]) =>
            apiClient.put<TaskStatus[], { statusIds: string[] }>('/api/tasks/statuses/reorder', { statusIds }),
        saveConfiguration: (input: TaskStatusConfigurationInput) =>
            apiClient.put<TaskStatus[], TaskStatusConfigurationInput>('/api/tasks/statuses/configuration', input),
        delete: (statusId: string) => apiClient.delete<void>(`/api/tasks/statuses/${statusId}`),
    },
};
